import classNames from "classnames";
import isArray from "lodash/isArray";
import { Stack } from "PFComponents/containers";
import { Select, SelectCommonProps } from "PFComponents/select";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AccessLevel, BookingCategory, FeatureFlag } from "PFTypes";
import { Option } from "PFTypes/options";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CategoryOption } from "../booking_form/booking_category_select/category_option";
import css from "./booking_category_select.module.scss";

const defaultFilterBookingCategory = ({ bookings_permissions: bookingPermissions }: BookingCategory) =>
  bookingPermissions === AccessLevel.ReadWrite;

type BookingCategorySelectProps = Omit<
  SelectCommonProps<BookingCategory>,
  "onChange" | "label" | "disabled" | "onClear" | "tip"
> & {
  category: BookingCategory | null;
  getTip?: (data: { options: Option<BookingCategory>[] }) => string | undefined;
  onCategoryChange?: (value: BookingCategory | null) => void;
  demand?: boolean;
  label?: string;
  letClear?: boolean;
  restrictedTextWidth?: boolean;
  disabled?: boolean;
  withoutLabel?: boolean;
  filterBookingCategory?: (category: BookingCategory) => boolean;
};

export const BookingCategorySelect = ({
  category,
  className,
  getTip,
  onCategoryChange,
  demand,
  label,
  letClear,
  restrictedTextWidth,
  disabled,
  withoutLabel,
  filterBookingCategory = defaultFilterBookingCategory,
  ...selectProps
}: BookingCategorySelectProps) => {
  const { t } = useTranslation("bookingModule");
  const isBookingModuleEnabled = useIsFeatureEnabled()(FeatureFlag.BookingModule);
  const { bookingCategories, getBookingCategory } = useBookingCategories();

  const options: Option<BookingCategory>[] = useMemo(() => {
    let options;
    if (!isBookingModuleEnabled) {
      options = bookingCategories.filter((category) => category.main);
    } else {
      options = bookingCategories
        .filter(
          (category) =>
            !category.archived && (demand ? category.activity_assignable : !category.activity_assignable)
        )
        .filter(filterBookingCategory);
    }
    return options.map((category) => ({
      id: category.id,
      value: category.display_as,
      displayElement: (
        <CategoryOption
          category={category}
          restrictedTextWidth={restrictedTextWidth}
          className={css.option}
        />
      ),
      original: category
    }));
  }, [bookingCategories, demand, restrictedTextWidth, isBookingModuleEnabled, filterBookingCategory]);

  const mainCategory = bookingCategories.find(({ main }) => main);
  const value = isBookingModuleEnabled ? category?.id : mainCategory?.id;

  return (
    <Stack
      className={classNames(css.root, className, { [css.tableVariant]: selectProps.variant === "table" })}
    >
      <Select<BookingCategory>
        {...selectProps}
        label={withoutLabel ? undefined : label || t("bookings.create.selectCategory")}
        disabled={!isBookingModuleEnabled || disabled}
        renderDisplayValue={(option) => {
          if (!option && !!value) {
            // selected category is not available in dropdown options
            const nonAvailableCategory = getBookingCategory(value);
            return nonAvailableCategory ? (
              <CategoryOption category={nonAvailableCategory} restrictedTextWidth={restrictedTextWidth} />
            ) : null;
          }
          return (
            !!option?.id &&
            !!option.original && (
              <CategoryOption category={option.original} restrictedTextWidth={restrictedTextWidth} />
            )
          );
        }}
        onChange={(option) => {
          const categoryOption = isArray(option) ? option[0] : option;
          onCategoryChange?.(categoryOption?.original ?? null);
        }}
        onClear={letClear && !!category ? () => onCategoryChange?.(null) : undefined}
        tip={getTip?.({ options })}
        value={value}
        options={options}
      />
    </Stack>
  );
};
